// Import Redux
import { Action, Dispatch } from 'redux';
// Import Services
import { StringService } from '@app/services';
// Import Action Type Enumeration
import ActionType from '@app/redux/actions/types';
import { NotificationType } from '@app/enumerations';
// Import Api Models
import {
    EligibilityMessagePriority,
    EligibilityMessagePriorityAddRequest,
    EligibilityMessagePriorityWithId,
} from '@app/models/api';
// Import Controller Factory
import ControllerFactoryProvider from '@app/controllers/factory';
// Import Action Methods
import {
    addNotification,
    endProcessing,
    startProcessing,
} from '@app/redux/actions/methods';
// Import Action Parameters
import { OverlayTargetParameters } from './ActionParametersBarrel';

//#region Load Eligibility Message Priorities Action
interface LoadEligibilityMessagePrioritiesAction extends Action {
    eligibilityMessagePriorities: EligibilityMessagePriorityWithId[];
}

const loadEligibilityMessagePrioritiesAction: (
    eligibilityMessagePriorities: EligibilityMessagePriorityWithId[],
    params: OverlayTargetParameters
) => LoadEligibilityMessagePrioritiesAction = (
    eligibilityMessagePriorities: EligibilityMessagePriorityWithId[],
    params: OverlayTargetParameters
) => ({
    ...params,
    eligibilityMessagePriorities,
    type: ActionType.LOAD_ELIGIBILITY_MESSAGE_PRIORITIES,
});

const loadEligibilityMessagePriorities =
    (params: OverlayTargetParameters) =>
    async (dispatch: Dispatch): Promise<EligibilityMessagePriorityWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-loadEligibilityMessagePriorities`;
        try {
            await startProcessing({
                message: 'Loading Eligibility Message Priorities table...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            const eligibilityMessagePriorities = await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .getEligibilityMessagePriorities();

            // Load to redux.
            dispatch<LoadEligibilityMessagePrioritiesAction>(loadEligibilityMessagePrioritiesAction(eligibilityMessagePriorities, params));

            await endProcessing({ 
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return eligibilityMessagePriorities;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion

//#region Delete Eligibility Message Priority Action

interface DeleteEligibilityMessagePriorityParameters extends OverlayTargetParameters {
    eligibilityMessagePriorityId: string;
}

const deleteEligibilityMessagePriority = (params: DeleteEligibilityMessagePriorityParameters) =>
    async (dispatch: Dispatch): Promise<EligibilityMessagePriorityWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-deleteEligibilityMessagePriority`;
        try {
            await startProcessing({
                message: 'Removing Eligibility Message Priority...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .deleteEligibilityMessagePriority(params.eligibilityMessagePriorityId);

            const eligibilityMessagePriorities = await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .getEligibilityMessagePriorities();
            // load to redux
            dispatch<LoadEligibilityMessagePrioritiesAction>(loadEligibilityMessagePrioritiesAction(eligibilityMessagePriorities, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return eligibilityMessagePriorities;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion

//#region Add Eligibility Message Priority Action

interface AddEligibilityMessagePriorityParameters extends OverlayTargetParameters {
    eligibilityMessagePriorityToAdd: EligibilityMessagePriorityAddRequest;
}

const addEligibilityMessagePriority = (params: AddEligibilityMessagePriorityParameters) =>
    async (dispatch: Dispatch): Promise<EligibilityMessagePriorityWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(
            params
        )}-addEligibilityMessagePriority`;
        try {
            await startProcessing({
                message: 'Saving Eligibility Message Priority information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .addEligibilityMessagePriority(
                    params.eligibilityMessagePriorityToAdd
                );

            const eligibilityMessagePriorities = await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .getEligibilityMessagePriorities();

            dispatch<LoadEligibilityMessagePrioritiesAction>(loadEligibilityMessagePrioritiesAction(eligibilityMessagePriorities, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return eligibilityMessagePriorities;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion

//#region Update Eligibility Message Priority

interface EditEligibilityMessagePriorityParameters extends OverlayTargetParameters {
    eligibilityMessagePriorityToEdit: EligibilityMessagePriority;
    eligibilityMessagePriorityId: string;
}

const updateEligibilityMessagePriority = (params: EditEligibilityMessagePriorityParameters) =>
    async (dispatch: Dispatch): Promise<EligibilityMessagePriorityWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-updateEligibilityMessagePriority`;
        try {
            await startProcessing({
                message: 'Saving eligibility message priority information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .updateEligibilityMessagePriority(
                    params.eligibilityMessagePriorityId,
                    params.eligibilityMessagePriorityToEdit
                );

            const eligibilityMessagePriorities = await ControllerFactoryProvider
                .getControllerFactory()
                .getEligibilityMessagePriorityController()
                .getEligibilityMessagePriorities();
            // Load to redux.
            dispatch<LoadEligibilityMessagePrioritiesAction>(loadEligibilityMessagePrioritiesAction(eligibilityMessagePriorities, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return eligibilityMessagePriorities;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion

//#region Select Eligibility Message Priority Action

interface SelectEligibilityMessagePriorityParameters {
    eligibilityMessagePriorityId: string;
}

interface SelectEligibilityMessagePriorityAction extends Action {
    eligibilityMessagePriorityId: string;
}

const selectEligibilityMessagePriorityAction: (
    params: SelectEligibilityMessagePriorityParameters
) => SelectEligibilityMessagePriorityAction = (
    params: SelectEligibilityMessagePriorityParameters
) => ({
    ...params,
    type: ActionType.SELECT_ELIGIBILITY_MESSAGE_PRIORITY,
});

const selectEligibilityMessagePriority = (params: SelectEligibilityMessagePriorityParameters) =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // Select on redux.
            dispatch<SelectEligibilityMessagePriorityAction>(selectEligibilityMessagePriorityAction(params));
        }
        catch (error: any) {
            throw error;
        }
    };

//#endregion

//#region Unselect Eligibility Message Priority Action

interface UnselectEligibilityMessagePriorityParameters {
    eligibilityMessagePriorityId: string;
}

type UnselectEligibilityMessagePriorityAction = Action;

const unselectEligibilityMessagePriorityAction: (
    params: UnselectEligibilityMessagePriorityParameters
) => UnselectEligibilityMessagePriorityAction = (
    params: UnselectEligibilityMessagePriorityParameters
) => ({
    ...params,
    type: ActionType.UNSELECT_ELIGIBILITY_MESSAGE_PRIORITY,
});

const unselectEligibilityMessagePriority = (params: UnselectEligibilityMessagePriorityParameters) =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // Unselect on redux.
            dispatch<UnselectEligibilityMessagePriorityAction>(unselectEligibilityMessagePriorityAction(params));
        }
        catch (error: any) {
            throw error;
        }
    };

//#endregion

//#region Export actions.
export {
    LoadEligibilityMessagePrioritiesAction,
    SelectEligibilityMessagePriorityAction,
    UnselectEligibilityMessagePriorityAction,
};
//#endregion

//#region Export action methods.
export {
    addEligibilityMessagePriority,
    deleteEligibilityMessagePriority,
    loadEligibilityMessagePriorities,
    selectEligibilityMessagePriority,
    updateEligibilityMessagePriority,
    unselectEligibilityMessagePriority,
};
//#endregion

//#region Export action parameters.
export {
    AddEligibilityMessagePriorityParameters,
    DeleteEligibilityMessagePriorityParameters,
    EditEligibilityMessagePriorityParameters,
    SelectEligibilityMessagePriorityParameters,
    UnselectEligibilityMessagePriorityParameters,
};
//#endregion

//#region Export action factories.
export {
    loadEligibilityMessagePrioritiesAction,
    selectEligibilityMessagePriorityAction,
    unselectEligibilityMessagePriorityAction,
};
//#endregion
